/**
 * SendForSignatureModal — pick a contract template, fill the merge fields,
 * confirm recipients, fire it through DocuSign (or mock mode).
 *
 * Used from the Pipeline card and from the Contracts page "+ New" button.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileSignature, Send, Loader2, FileText, Search, X } from "lucide-react";

const FIELD_LABELS = {
  property_address: "Property Address",
  purchase_price: "Purchase Price ($)",
  assignment_fee: "Assignment Fee ($)",
  earnest_money: "Earnest Money ($)",
  close_date: "Close Date",
  inspection_period_days: "Inspection Period (days)",
  split_percentage_a: "Partner A Split (%)",
  split_percentage_b: "Partner B Split (%)",
};

const FIELD_TYPES = {
  property_address: "text",
  purchase_price: "number",
  assignment_fee: "number",
  earnest_money: "number",
  close_date: "date",
  inspection_period_days: "number",
  split_percentage_a: "number",
  split_percentage_b: "number",
};

export default function SendForSignatureModal({
  open,
  onOpenChange,
  lead = null,
  prefill = null,
  onSent,
}) {
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("assignment");
  const [recipients, setRecipients] = useState([]);
  const [merge, setMerge] = useState({});
  const [specialConditions, setSpecialConditions] = useState("");
  const [recentSnippets, setRecentSnippets] = useState([]);
  // Source toggle: "builtin" = our 3 templates; "docusign" = user's
  // own templates pulled from their connected DocuSign account.
  const [source, setSource] = useState("builtin");
  const [dsTemplates, setDsTemplates] = useState([]);
  const [dsTemplatesLoading, setDsTemplatesLoading] = useState(false);
  const [dsTemplatesError, setDsTemplatesError] = useState("");
  const [dsSearch, setDsSearch] = useState("");
  const [dsSelected, setDsSelected] = useState(null); // {template_id, name, roles, ...}
  const [dsSelectedLoading, setDsSelectedLoading] = useState(false);
  const [dsRoles, setDsRoles] = useState([]); // [{role_name, name, email}]
  const [busy, setBusy] = useState(false);

  const buildPrefill = (tpl, leadObj) => {
    const recipientsInit = tpl.recipients.map((r) => ({
      role: r.role,
      label: r.label,
      name: "",
      email: "",
    }));
    const mergeInit = {};
    if (leadObj) {
      mergeInit.property_address = `${leadObj.property_address || ""}${leadObj.city ? `, ${leadObj.city}` : ""}${leadObj.state ? `, ${leadObj.state}` : ""}`.trim().replace(/^, /, "");
      if (leadObj.purchase_price) mergeInit.purchase_price = leadObj.purchase_price;
      if (leadObj.assignment_fee) mergeInit.assignment_fee = leadObj.assignment_fee;
    }
    return { recipientsInit, mergeInit };
  };

  // load template catalog when modal opens — also primes recipients + merge
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const [tplRes, snipRes] = await Promise.all([
          api.get("/docusign/templates"),
          api.get("/docusign/special-conditions/recent").catch(() => ({ data: { snippets: [] } })),
        ]);
        if (!alive) return;
        const list = tplRes.data?.templates || [];
        setTemplates(list);
        setRecentSnippets(snipRes.data?.snippets || []);
        // Honour `prefill.template_key` if the caller pinned one (e.g. the
        // Community Wall always wants the assignment template).
        const startKey = prefill?.template_key || templateKey;
        const initialTpl = list.find((t) => t.key === startKey) || list[0];
        if (initialTpl) {
          const { recipientsInit, mergeInit } = buildPrefill(initialTpl, lead);
          // Apply pre-filled recipient name+email by matching `role`. Lets
          // the Community Wall (or any caller) deep-link both signers.
          if (prefill?.recipients?.length) {
            const byRole = Object.fromEntries(
              prefill.recipients.map((r) => [r.role, r])
            );
            for (const slot of recipientsInit) {
              const pre = byRole[slot.role];
              if (pre) {
                slot.name = pre.name || "";
                slot.email = pre.email || "";
              }
            }
          }
          setTemplateKey(initialTpl.key);
          setRecipients(recipientsInit);
          setMerge(mergeInit);
          setSpecialConditions("");
        }
      } catch (e) {
        toast.error("Couldn't load contract templates");
      }
    })();
    return () => { alive = false; };
    // Intentional: re-run only when modal opens. Including `lead` /
    // `templateKey` would re-fetch + reset the form on every field change.
  }, [open]);

  const selected = useMemo(
    () => templates.find((t) => t.key === templateKey),
    [templates, templateKey]
  );

  const pickTemplate = (key) => {
    const tpl = templates.find((t) => t.key === key);
    if (!tpl) return;
    const { recipientsInit, mergeInit } = buildPrefill(tpl, lead);
    setTemplateKey(key);
    setRecipients(recipientsInit);
    setMerge(mergeInit);
  };

  const updateRecipient = (idx, field, value) => {
    setRecipients((arr) => arr.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const updateMerge = (key, value) => {
    setMerge((m) => ({ ...m, [key]: value }));
  };

  const canSend = useMemo(() => {
    if (source === "docusign") {
      if (!dsSelected) return false;
      if (dsRoles.length === 0) return false;
      return dsRoles.every((r) => r.name && r.email);
    }
    if (!selected) return false;
    if (recipients.some((r) => !r.name || !r.email)) return false;
    if (selected.required_fields.some((k) => !merge[k] && merge[k] !== 0)) return false;
    return true;
  }, [source, selected, recipients, merge, dsSelected, dsRoles]);

  // Lazy-load DocuSign account templates when the user flips to that source.
  const loadDsTemplates = async (q = "") => {
    setDsTemplatesLoading(true);
    setDsTemplatesError("");
    try {
      const { data } = await api.get("/docusign/account-templates", {
        params: q ? { search: q, limit: 100 } : { limit: 100 },
      });
      setDsTemplates(data?.templates || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to load DocuSign templates";
      setDsTemplates([]);
      setDsTemplatesError(detail);
    } finally {
      setDsTemplatesLoading(false);
    }
  };

  const flipSource = (next) => {
    setSource(next);
    if (next === "docusign" && dsTemplates.length === 0 && !dsTemplatesError) {
      loadDsTemplates();
    }
  };

  const pickDsTemplate = async (tpl) => {
    setDsSelected(tpl);
    setDsSelectedLoading(true);
    try {
      const { data } = await api.get(`/docusign/account-templates/${tpl.template_id}`);
      setDsSelected(data);
      // Initialise blank recipient slots, one per role.
      setDsRoles((data.roles || []).map((r) => ({
        role_name: r.role_name, name: "", email: "",
      })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load template detail");
      setDsSelected(null);
      setDsRoles([]);
    } finally {
      setDsSelectedLoading(false);
    }
  };

  const updateDsRole = (idx, field, value) => {
    setDsRoles((arr) => arr.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      if (source === "docusign") {
        const payload = {
          lead_id: lead?.id || null,
          template_id: dsSelected.template_id,
          template_roles: dsRoles.map((r) => ({
            role_name: r.role_name, name: r.name, email: r.email,
          })),
        };
        const { data } = await api.post("/docusign/send-from-account-template", payload);
        toast.success("Envelope sent for signature");
        onSent?.(data);
        onOpenChange(false);
        return;
      }
      const payload = {
        lead_id: lead?.id || null,
        template_key: selected.key,
        recipients: recipients.map((r) => ({
          name: r.name, email: r.email, role: r.role,
        })),
        merge_fields: { ...merge },
        special_conditions: specialConditions.trim() || null,
      };
      // Coerce numeric strings → numbers for cleaner Mongo storage.
      for (const k of Object.keys(payload.merge_fields)) {
        if (FIELD_TYPES[k] === "number") {
          const n = Number(payload.merge_fields[k]);
          if (!Number.isNaN(n)) payload.merge_fields[k] = n;
        }
      }
      const { data } = await api.post("/docusign/send", payload);
      toast.success(
        data.is_mock
          ? "Envelope sent (mock mode — connect DocuSign for real signing)"
          : "Envelope sent for signature"
      );
      onSent?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send envelope");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-signature-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature size={20} className="text-emerald-600" />
            Send Contract for Signature
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Source picker — built-in vs DocuSign account */}
          <div className="flex items-center gap-1 p-1 bg-[var(--bg)] rounded-lg border border-[var(--line)] w-fit" data-testid="signature-source-toggle">
            <button
              type="button"
              onClick={() => flipSource("builtin")}
              className={
                "px-3 py-1.5 rounded-md text-xs font-semibold transition " +
                (source === "builtin"
                  ? "bg-white text-[var(--ink)] shadow-sm"
                  : "text-[var(--ink-2)] hover:text-[var(--ink)]")
              }
              data-testid="source-builtin-btn"
            >
              <FileSignature size={12} className="inline mr-1.5" />
              Built-in templates
            </button>
            <button
              type="button"
              onClick={() => flipSource("docusign")}
              className={
                "px-3 py-1.5 rounded-md text-xs font-semibold transition " +
                (source === "docusign"
                  ? "bg-white text-[var(--ink)] shadow-sm"
                  : "text-[var(--ink-2)] hover:text-[var(--ink)]")
              }
              data-testid="source-docusign-btn"
            >
              <FileText size={12} className="inline mr-1.5" />
              From DocuSign
            </button>
          </div>

          {/* ── BUILT-IN MODE ── */}
          {source === "builtin" && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)]">Template</Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="template-picker">
              {templates.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => pickTemplate(t.key)}
                  data-testid={`template-${t.key}`}
                  className={
                    "p-3 rounded-md border text-left transition " +
                    (templateKey === t.key
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-[var(--line)] hover:border-emerald-300 bg-white")
                  }
                >
                  <div className="font-semibold text-sm">{t.label}</div>
                  <div className="text-xs text-[var(--ink-2)] mt-1">{t.description}</div>
                </button>
              ))}
            </div>
          </div>
          )}

          {/* ── DOCUSIGN ACCOUNT TEMPLATES MODE ── */}
          {source === "docusign" && (
            <div data-testid="docusign-template-picker">
              <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)]">
                Your DocuSign Templates
              </Label>
              {dsTemplatesError ? (
                <div className="mt-2 p-3 rounded-md border border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="ds-templates-error">
                  {dsTemplatesError}
                </div>
              ) : (
                <>
                  {/* Search */}
                  <div className="mt-2 relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
                    <Input
                      className="pl-8"
                      placeholder="Search templates by name…"
                      value={dsSearch}
                      onChange={(e) => setDsSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); loadDsTemplates(dsSearch); }
                      }}
                      data-testid="ds-template-search"
                    />
                  </div>

                  {dsTemplatesLoading ? (
                    <div className="mt-3 p-6 text-center text-sm text-[var(--ink-2)]">
                      <Loader2 size={16} className="animate-spin inline mr-2" />
                      Loading your DocuSign templates…
                    </div>
                  ) : dsTemplates.length === 0 ? (
                    <div className="mt-3 p-6 text-center text-sm text-[var(--ink-2)] border border-dashed border-[var(--line)] rounded-md">
                      No templates found in your DocuSign account.
                      <div className="text-xs mt-1">
                        Create one in DocuSign → Templates, then click search again.
                      </div>
                    </div>
                  ) : dsSelected ? (
                    <div className="mt-2 p-3 rounded-md border border-emerald-500 bg-emerald-50" data-testid="ds-template-selected">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{dsSelected.name}</div>
                          {dsSelected.description && (
                            <div className="text-xs text-[var(--ink-2)] mt-1 line-clamp-2">{dsSelected.description}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => { setDsSelected(null); setDsRoles([]); }}
                          className="p-1 hover:bg-emerald-100 rounded shrink-0"
                          title="Change template"
                          data-testid="ds-template-clear"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 max-h-56 overflow-y-auto border border-[var(--line)] rounded-md divide-y divide-[var(--line)]" data-testid="ds-template-list">
                      {dsTemplates.map((t) => (
                        <button
                          key={t.template_id}
                          type="button"
                          onClick={() => pickDsTemplate(t)}
                          className="block w-full text-left p-3 hover:bg-emerald-50 transition"
                          data-testid={`ds-template-${t.template_id}`}
                        >
                          <div className="font-semibold text-sm truncate">{t.name}</div>
                          {t.description ? (
                            <div className="text-xs text-[var(--ink-2)] mt-0.5 line-clamp-1">{t.description}</div>
                          ) : (
                            <div className="text-xs text-[var(--ink-2)] mt-0.5 italic">{t.email_subject || `${t.page_count || 0} pages`}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── BUILT-IN: Recipients ── */}
          {source === "builtin" && selected && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)]">Recipients</Label>
              <div className="mt-2 space-y-3">
                {recipients.map((r, idx) => (
                  <div key={r.role} className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-md border border-[var(--line)]">
                    <div className="sm:col-span-2 text-xs font-semibold text-[var(--ink-2)]">{r.label}</div>
                    <Input
                      placeholder="Full Name"
                      value={r.name}
                      onChange={(e) => updateRecipient(idx, "name", e.target.value)}
                      data-testid={`recipient-${r.role}-name`}
                    />
                    <Input
                      placeholder="email@example.com"
                      type="email"
                      value={r.email}
                      onChange={(e) => updateRecipient(idx, "email", e.target.value)}
                      data-testid={`recipient-${r.role}-email`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── DOCUSIGN: Recipients (dynamic per template role) ── */}
          {source === "docusign" && dsSelected && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)]">Recipients</Label>
              {dsSelectedLoading ? (
                <div className="mt-2 p-4 text-center text-sm text-[var(--ink-2)]">
                  <Loader2 size={14} className="animate-spin inline mr-2" /> Loading template roles…
                </div>
              ) : (
                <div className="mt-2 space-y-3" data-testid="ds-roles">
                  {dsRoles.map((r, idx) => (
                    <div key={r.role_name} className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-md border border-[var(--line)]">
                      <div className="sm:col-span-2 text-xs font-semibold text-[var(--ink-2)]">{r.role_name}</div>
                      <Input
                        placeholder="Full Name"
                        value={r.name}
                        onChange={(e) => updateDsRole(idx, "name", e.target.value)}
                        data-testid={`ds-role-${r.role_name}-name`}
                      />
                      <Input
                        placeholder="email@example.com"
                        type="email"
                        value={r.email}
                        onChange={(e) => updateDsRole(idx, "email", e.target.value)}
                        data-testid={`ds-role-${r.role_name}-email`}
                      />
                    </div>
                  ))}
                  {dsRoles.length === 0 && (
                    <div className="text-xs text-[var(--ink-2)] italic">
                      This template has no recipient roles defined in DocuSign.
                    </div>
                  )}
                </div>
              )}
              <div className="text-[10px] text-[var(--ink-2)] mt-2 italic">
                Contract content, fields, and signature placement come from your DocuSign template.
              </div>
            </div>
          )}

          {/* ── BUILT-IN: Merge fields + Special Conditions ── */}
          {source === "builtin" && selected && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)]">Contract Fields</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selected.required_fields.map((k) => (
                  <div key={k}>
                    <Label className="text-xs text-[var(--ink-2)]">{FIELD_LABELS[k] || k}</Label>
                    <Input
                      type={FIELD_TYPES[k] || "text"}
                      value={merge[k] ?? ""}
                      onChange={(e) => updateMerge(k, e.target.value)}
                      data-testid={`merge-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {source === "builtin" && selected && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)]">
                Special Conditions <span className="normal-case text-[10px] text-[var(--ink-2)] tracking-normal">(optional rider)</span>
              </Label>

              {recentSnippets.length > 0 && (
                <div className="mt-2" data-testid="special-conditions-snippets">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] mb-1.5">
                    Recent — click to insert
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {recentSnippets.map((s, i) => {
                      const preview = s.text.length > 60 ? `${s.text.slice(0, 60)}…` : s.text;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSpecialConditions(s.text)}
                          title={s.text + (s.count > 1 ? ` (used ${s.count}×)` : "")}
                          data-testid={`snippet-${i}`}
                          className="text-[11px] px-2 py-1 rounded-full border border-[var(--line)] bg-white hover:border-emerald-400 hover:bg-emerald-50 text-[var(--ink-1)] transition max-w-full text-left truncate"
                        >
                          {preview}
                          {s.count > 1 && (
                            <span className="ml-1.5 text-emerald-600 font-semibold">{s.count}×</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <Textarea
                className="mt-2"
                rows={4}
                maxLength={4000}
                placeholder="e.g. Subject to assignor's review of inspection report within 5 business days. Buyer to deliver earnest money to ABC Title within 48 hours."
                value={specialConditions}
                onChange={(e) => setSpecialConditions(e.target.value)}
                data-testid="merge-special-conditions"
              />
              <div className="text-[10px] text-[var(--ink-2)] mt-1 text-right">
                {specialConditions.length}/4000
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="signature-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={!canSend || busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="signature-send-btn"
          >
            {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
            Send for Signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
